import {
  GameStatuses,
  type RelatedScheduleResponse,
  type ScoreboardResponse,
} from '@on3/api';
import {
  getGameTime,
  getPredictionOutcome,
  getTeamData,
} from '@on3/api/utils/games';
import { On3IconLock } from '@on3/icons';
import { Check, XMark } from '@on3/ui-lib/components/Svg';
import { useAuth, useSite } from '@on3/ui-lib/index';
import { handleKeyDown } from '@on3/ui-lib/utils/accessibility/keyDown';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { format, parse } from 'date-fns';
import { type GameActions, GameWrapper } from 'hocs/Game/GameWrapper';
import Link from 'next/link';
import { useCallback } from 'react';

import styles, { newGreen } from './ScheduleItem.module.scss';

interface IScheduleItemProps {
  game: ScoreboardResponse;
  relatedModel?: RelatedScheduleResponse;
}

const decimalToPercentage = (decimal: number | null | undefined) => {
  if (!decimal) return '-';

  return `${Math.round(decimal * 100)}%`;
};

export const ScheduleItem = ({ game, relatedModel }: IScheduleItemProps) => {
  const {
    currentOrgIsHome,
    currentOrgIsWinner,
    startDate,
    statusEnum,
    ticketLink,
    winner,
  } = game || {};

  const { currentSite } = useSite();
  const { user } = useAuth();
  const isMember = user?.has;

  const parsedDate = game?.startDateUtc
    ? new Date(game.startDateUtc * 1000)
    : startDate && parse(startDate, 'M/d', new Date());

  const gameDate = parsedDate && format(parsedDate, 'EEE M/d');
  const gameTime = getGameTime(game);

  const showScores = statusEnum !== GameStatuses.Scheduled;
  const predictionOutcome = getPredictionOutcome(game);

  const primaryColor =
    currentSite?.linkColor ?? currentSite?.primaryColor ?? '#fe3b1f';

  const homeTeam = getTeamData(true, game);
  const awayTeam = getTeamData(false, game);
  const currentTeam = currentOrgIsHome ? homeTeam : awayTeam;
  const sport = slugify(relatedModel?.sport?.name || 'football');

  const source = slugify(relatedModel?.organization?.name || 'college');
  const utmLink = `/join/?utm_source=${source}&utm_medium=web&utm_campaign=team_schedule&utm_content=subscribe_link`;

  const getTicketLink = useCallback(
    (link: string) => {
      const params = new URLSearchParams({
        utm_source: 'partner',
        utm_medium: 'affiliate',
        utm_campaign: 'on3',
        utm_content: `${awayTeam.slug}_vs_${homeTeam.slug}`,
        utm_term: `${sport}_tickets`,
      });

      return `${link}?${params}`;
    },
    [awayTeam.slug, homeTeam.slug, sport],
  );

  const getScoreStyle = (team: { key?: number }) => {
    if (team.key !== currentTeam.key || statusEnum !== GameStatuses.Completed)
      return;

    return currentOrgIsWinner ? styles.colorWin : styles.colorLoss;
  };

  const showRank = (ranking?: number | null) => {
    if (!ranking || ranking > 25) return false;

    return true;
  };

  return (
    <GameWrapper
      game={{
        ...game,
        currentTeam: {
          key: currentTeam.key as number,
          name: currentTeam.name as string,
          primaryColor,
        },
      }}
    >
      {(gameActions: GameActions) => (
        <tr
          className={styles.block}
          data-status={statusEnum}
          data-winner={winner?.key === homeTeam.key ? 'home' : 'away'}
        >
          <td headers="date">
            <div
              className={clsx(styles.date, {
                [styles.final]: statusEnum === GameStatuses.Completed,
              })}
              onClick={gameActions.edit}
              onKeyDown={(e) => handleKeyDown(e)}
              role={gameActions.edit && 'button'}
              tabIndex={gameActions.edit && 0}
            >
              <span>{gameDate}</span>
              <span className={styles.smallText}>{gameTime}</span>
            </div>
          </td>
          <td headers="opponent">
            <div>
              <div className={styles.away}>
                <span className={styles.ranking}>
                  {showRank(awayTeam.ranking.national) &&
                    `#${awayTeam.ranking.national}`}
                </span>
                <Link
                  data-away="name"
                  href={`/college/${awayTeam.slug}/${sport}/schedule/`}
                >
                  <figure>
                    <On3Avatar
                      altText={awayTeam?.name}
                      height={25}
                      source={getAssetUrl(awayTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span>{awayTeam?.name}</span>
                </Link>
                <span className={styles.smallText}>
                  ({awayTeam.record || '0-0'})
                </span>
              </div>
              <div className={styles.home}>
                <span className={styles.ranking}>
                  {showRank(homeTeam.ranking.national) &&
                    `#${homeTeam.ranking.national}`}
                </span>
                <Link
                  data-home="name"
                  href={`/college/${homeTeam.slug}/${sport}/schedule/`}
                >
                  <figure>
                    <On3Avatar
                      altText={homeTeam?.name}
                      height={25}
                      source={getAssetUrl(homeTeam?.avatar)}
                      teamAvatar
                      width={25}
                    />
                  </figure>
                  <span data-home="name">{homeTeam?.name}</span>
                </Link>
                <span className={styles.smallText}>
                  ({homeTeam.record || '0-0'})
                </span>
              </div>
            </div>
          </td>
          <td headers="score">
            {showScores || !ticketLink ? (
              <div
                onClick={gameActions.edit}
                onKeyDown={(e) => handleKeyDown(e)}
                role={gameActions.edit && 'button'}
                tabIndex={gameActions.edit && 0}
              >
                <span className={getScoreStyle(awayTeam)} data-away="score">
                  {showScores ? awayTeam.score : '-'}
                </span>
                <span className={getScoreStyle(homeTeam)} data-home="score">
                  {showScores ? homeTeam.score : '-'}
                </span>
              </div>
            ) : (
              <Link
                className={styles.tickets}
                href={getTicketLink(ticketLink)}
                rel="nofollow noopener noreferrer"
                style={{ color: primaryColor }}
                target="_blank"
              >
                <span>Get Tickets</span>
                <span>Ticketcity</span>
              </Link>
            )}
          </td>
          {isMember ? (
            <>
              <td headers="prediction">
                <div className={styles.predictions}>
                  <div>
                    <span data-away="prediction">
                      {awayTeam.prediction ?? '-'}
                    </span>
                    <span
                      data-away="probability"
                      data-content={decimalToPercentage(
                        awayTeam.winProbability,
                      )}
                    />
                  </div>
                  <div>
                    <span data-home="prediction">
                      {homeTeam.prediction ?? '-'}
                    </span>
                    <span
                      data-content={decimalToPercentage(
                        homeTeam.winProbability,
                      )}
                      data-home="probability"
                    />
                  </div>
                </div>
              </td>
              <td headers="winProbability">
                {statusEnum === GameStatuses.Completed ? (
                  <div
                    className={clsx(styles.result, {
                      [styles.correct]: predictionOutcome,
                      [styles.incorrect]: !predictionOutcome,
                    })}
                  >
                    {predictionOutcome ? <Check /> : <XMark />}
                  </div>
                ) : (
                  <div>
                    <span data-away="winProbability">
                      {decimalToPercentage(awayTeam.winProbability)}
                    </span>
                    <span data-home="winProbability">
                      {decimalToPercentage(homeTeam.winProbability)}
                    </span>
                  </div>
                )}
              </td>
            </>
          ) : (
            <td colSpan={2} headers="subscribe">
              <Link className={styles.callout} href={utmLink}>
                <div>
                  <On3IconLock fill={newGreen} />
                  <span className={styles.membersOnly}>members only</span>
                </div>
                <div className={styles.subscribe}>
                  <span>Subscribe</span> to unlock predictions.
                </div>
              </Link>
            </td>
          )}
        </tr>
      )}
    </GameWrapper>
  );
};
